/*
 * Types and helpers for the generated shell command manifest.
 *
 * The manifest is produced by tools/generate-shell-manifest.py from the spring-shell
 * sources, so the in-browser shell mirrors the CLI exactly. See ../generated/shell-manifest.json.
 */
import manifestData from "./generated/shell-manifest.json";

export type OptionType = "string" | "int" | "long" | "boolean" | "enum" | "list" | "file";

export interface ShellOption {
    name: string;
    type: OptionType;
    required: boolean;
    default?: string;
    enumValues?: string[];
}

export interface ShellQuery {
    param: string;
    optional: boolean;
    /** the option whose value fills this query param ... */
    fromOption?: string;
    /** ... or a constant the command hardcodes (e.g. an enum constant) */
    const?: string;
}

export interface ShellPathVar {
    /** the `{name}` placeholder in the path */
    name: string;
    /** the option whose value fills it ... */
    fromOption?: string;
    /** ... or a constant the command hardcodes */
    const?: string;
}

/**
 * Request-body descriptor:
 *  - `value`  – the body is a single option's value (a String -> text/plain, a list -> JSON array)
 *  - `const`  – a constant body (e.g. an empty list for "reset" commands)
 *  - `object` – a JSON object whose fields are filled from options (DTO / builder commands)
 */
export type ShellBody =
    | {kind: "value"; fromOption: string}
    | {kind: "const"; value: unknown}
    | {kind: "object"; fields: {name: string; value: ShellBody}[]}
    /** the body is the content of a locally picked file: whole text, non-blank lines, or raw bytes */
    | {kind: "file"; fromOption: string; encoding: "text" | "lines" | "bytes"};

export interface ShellMultipartPart {
    /** the multipart form field name expected by the backend */
    name: string;
    /**
     * `file` is filled from the picked local file; `field` from a string option;
     * `stringAsFile` from a string option whose value is sent as in-memory file content
     * (the CLI wraps it in a ByteArrayResource), so no file picker is involved
     */
    kind: "file" | "field" | "stringAsFile";
    fromOption: string;
}

export interface ShellMultipart {
    parts: ShellMultipartPart[];
}

export interface ShellDownload {
    /** the option that holds the desired download filename */
    filenameOption: string;
    /** if true the command also returns the content inline, so without a filename it is rendered */
    optional: boolean;
}

export interface ShellHttp {
    method: string;
    path: string;
    query: ShellQuery[];
    pathVars: ShellPathVar[];
    /** present when the command sends a request body */
    body: ShellBody | null;
    /** present when the command uploads a local file as multipart/form-data */
    multipart: ShellMultipart | null;
    /** present when the command returns a file to download */
    download: ShellDownload | null;
}

/** Reason a command cannot be executed from the web shell (it is still listed in help). */
export type Unsupported = "file" | "body" | "local" | "multi-call" | "unparsed";

export interface ShellCommand {
    words: string[];
    group: string;
    description: string;
    unsupported: Unsupported | null;
    /** the command is provided by the enterprise-only shell module; shown as "(pro)" in help */
    enterprise?: boolean;
    /** the command post-processes the response client-side; needs a ShellPostProcess transform */
    clientLogic?: boolean;
    options: ShellOption[];
    http: ShellHttp | null;
}

export interface ShellManifest {
    version: number;
    generatedFrom: string;
    commands: ShellCommand[];
}

export const manifest: ShellManifest = manifestData as ShellManifest;

/** Commands sorted by their textual form, useful for help/listing. */
export const commands: ShellCommand[] = manifest.commands;

const maxWords = commands.reduce((m, c) => Math.max(m, c.words.length), 0);

/**
 * Greedily match the longest leading run of tokens that equals a known command's words.
 * Returns the matched command and the number of tokens it consumed, or null.
 */
export function matchCommand(tokens: string[]): { command: ShellCommand; consumed: number } | null {
    for (let n = Math.min(maxWords, tokens.length); n >= 1; n--) {
        const candidate = tokens.slice(0, n).join(" ");
        const command = commands.find(c => c.words.join(" ") === candidate);
        if (command) {
            return { command, consumed: n };
        }
    }
    return null;
}

/** Human-readable reason text for an unsupported command. */
export function unsupportedReason(reason: Unsupported): string {
    switch (reason) {
        case "file": return "requires a local file (import/export) and is not available in the web shell";
        case "body": return "sends a structured request body and is not available in the web shell";
        case "local": return "is a CLI-local command (configures the standalone shell) with no server action";
        case "multi-call": return "performs multiple back-end calls and is not available in the web shell";
        default: return "could not be mapped to a single REST call and is not available in the web shell";
    }
}
